import { useState, useEffect, useRef } from 'react';
import { showService } from '../services/showService';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { PlatformType, Show } from "../types/Show";

interface UseUserShowsReturn {
  shows: Show[];
  loading: boolean;
  error: string | null;
  hasNext: boolean;
  refetch: (count?: number, offset?: number) => Promise<void>;
}

export const useUserShows = (
  count: number = 20,
  offset: number = 0,
  platformType: PlatformType = PlatformType.DASHREEL,
  nameQuery: string = ''
): UseUserShowsReturn => {
  const { isAuth, loading: authLoading } = useAuth();
  const [shows, setShows] = useState<Show[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasNext, setHasNext] = useState(true); // Assume there might be more initially
  // Search requests can resolve out of order; only the latest request may update state.
  const latestRequestId = useRef(0);

  const fetchShows = async (fetchCount: number = count, fetchOffset: number = offset) => {
    const requestId = ++latestRequestId.current;
    const isLatest = () => requestId === latestRequestId.current;

    if (!isAuth) {
      setShows([]);
      setLoading(false);
      setError(null);
      setHasNext(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response: Show[] = await showService.getUserShows(fetchCount, fetchOffset, platformType, nameQuery);
      if (!isLatest()) return;

      setShows(response || []);
      // If we get fewer shows than requested, or empty array, we've reached the end
      setHasNext(response && response.length === fetchCount);
    } catch (err) {
      if (!isLatest()) return;
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch user shows';
      setError(errorMessage);
      console.error('Error fetching user shows:', err);
      setShows([]);
      setHasNext(false);
    } finally {
      if (isLatest()) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!authLoading) {
      fetchShows(count, offset);
    }
  }, [count, offset, platformType, nameQuery, isAuth, authLoading]);

  return {
    shows,
    loading,
    error,
    hasNext,
    refetch: fetchShows
  };
};
